import type { FC, ReactNode } from 'react'
import type { PostSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { CategoryLink } from '@/components/blog/category-link'
import { CoverImage } from '@/components/blog/cover-image'
import { PostMeta } from '@/components/blog/post-meta'

interface FeaturedStoryProps {
    post: PostSummary
}

export const FeaturedStory: FC<FeaturedStoryProps> = ({ post }): ReactNode => {
    return (
        <article className='group relative flex flex-col'>
            <div className='relative aspect-[16/11] overflow-hidden rounded-2xl bg-muted'>
                <CoverImage
                    src={post.coverImage}
                    alt={post.coverImageAlt}
                    priority
                    className='transition-transform duration-1000 ease-out group-hover:scale-[1.025] motion-reduce:transition-none motion-reduce:group-hover:scale-100'
                />

                <span className='absolute left-4 top-4 inline-flex items-center rounded-full bg-background/90 px-3 py-1.5 text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-foreground backdrop-blur sm:left-5 sm:top-5'>
                    Lead story
                </span>
            </div>

            <div className='mt-7 flex flex-col'>
                {post.category && <CategoryLink category={post.category} />}

                <h3 className='mt-3 font-heading text-[2.15rem] font-medium leading-[1.05] tracking-tight text-balance sm:text-5xl'>
                    <Link
                        href={`/blog/${post.slug}`}
                        className='rounded-sm underline-offset-[0.15em] decoration-brand/70 decoration-2 after:absolute after:inset-0 after:rounded-2xl group-hover:underline focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-4 focus-visible:after:outline-ring'
                    >
                        {post.title}
                    </Link>
                </h3>

                {post.excerpt && (
                    <p className='mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground'>
                        {post.excerpt}
                    </p>
                )}

                <div className='mt-7 flex flex-wrap items-center justify-between gap-5'>
                    <PostMeta
                        author={post.author}
                        publishedAt={post.publishedAt}
                        publishedLabel={post.publishedLabel}
                        readingTime={post.readingTime}
                        size='md'
                    />

                    <span aria-hidden='true' className='inline-flex items-center gap-2 text-sm font-semibold text-foreground'>
                        Read the story
                        <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-1' />
                    </span>
                </div>
            </div>
        </article>
    )
}
